import { isNotDefaultValue, isRoundComplete } from '../math/spadesMath';
import type { Round } from '../../types';

const KEY = 'analytics_real_game_v1';
const MIN_PAUSE_MS = 60_000;
const MAX_PAUSE_MS = 2 * 60 * 60_000;
type State = {
  round: number;
  bidAt: number | null;
  firstMadeSeen: boolean;
  eligible: boolean;
  qualifiedRounds: number[];
  emitted: boolean;
};
const empty = (): State => ({
  round: -1,
  bidAt: null,
  firstMadeSeen: false,
  eligible: false,
  qualifiedRounds: [],
  emitted: false,
});
let fallback = empty();
function read(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return empty();
    const state = JSON.parse(raw) as State;
    if (
      Array.isArray(state.qualifiedRounds) &&
      typeof state.emitted === 'boolean' &&
      typeof state.round === 'number'
    )
      return state;
  } catch {
    /* storage is optional */
  }
  return fallback;
}
function save(state: State) {
  fallback = state;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* best effort */
  }
}
export function resetRealGameAnalytics() {
  save(empty());
}
function bidsComplete(round: Round) {
  return [
    round.team1BidsAndMade.p1Bid,
    round.team1BidsAndMade.p2Bid,
    round.team2BidsAndMade.p1Bid,
    round.team2BidsAndMade.p2Bid,
  ].every(isNotDefaultValue);
}
function hasMade(round: Round) {
  return [
    round.team1BidsAndMade.p1Made,
    round.team1BidsAndMade.p2Made,
    round.team2BidsAndMade.p1Made,
    round.team2BidsAndMade.p2Made,
  ].some(isNotDefaultValue);
}
/** Called only for local/leader user edits to the current round. */
export function recordScoringTransition(
  before: Round,
  after: Round,
  round: number,
  now: number,
  emit: () => void,
) {
  const state = read();
  if (state.round !== round) {
    state.round = round;
    state.bidAt = null;
    state.firstMadeSeen = false;
    state.eligible = false;
  }
  if (!bidsComplete(after) && !state.firstMadeSeen) state.bidAt = null;
  if (
    !bidsComplete(before) &&
    bidsComplete(after) &&
    !hasMade(before) &&
    !hasMade(after) &&
    !state.firstMadeSeen
  )
    state.bidAt = now;
  if (!hasMade(before) && hasMade(after) && !state.firstMadeSeen) {
    state.firstMadeSeen = true;
    const pause = state.bidAt === null ? -1 : now - state.bidAt;
    state.eligible =
      bidsComplete(after) && pause >= MIN_PAUSE_MS && pause <= MAX_PAUSE_MS;
  }
  if (
    state.eligible &&
    isRoundComplete(after) &&
    !state.qualifiedRounds.includes(round)
  ) {
    state.qualifiedRounds.push(round);
    if (state.qualifiedRounds.length >= 2 && !state.emitted) {
      state.emitted = true;
      save(state);
      emit();
      return;
    }
  }
  save(state);
}
